import React, { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, ButtonBase, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';

import type { LensCardType } from '~/types/leLens';

import ChartTypeGlyph from './ChartTypeGlyph';
import { CARD_TYPE_GUIDANCE } from './cardTypeGuidance';
import { cardTypeLabel } from './planGateResolve';

/**
 * The chart type as one tile showing the drawing it will produce, opening onto
 * every type it could be instead.
 *
 * A row of small glyphs made the reader compare four drawings to find the one
 * already chosen. The tile states the choice; the menu is where the choosing
 * happens, and it has room for the sentence saying what each type is for.
 */
export function RowTypePicker({
  compact = false,
  disabled,
  onChoose,
  title,
  types,
  value,
}: {
  compact?: boolean;
  disabled: boolean;
  onChoose: (type: LensCardType) => void;
  title: string;
  types: ReadonlyArray<LensCardType>;
  value: LensCardType;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <ButtonBase
        aria-haspopup="listbox"
        aria-label={`Chart type for ${title}: ${cardTypeLabel(value)}`}
        disabled={disabled}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          color: 'text.secondary',
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          flexShrink: 0,
          gap: 0.25,
          // Fixed, so a two-line name ("Stacked bar chart") cannot make one
          // row taller than the rest of the plan.
          height: compact ? 36 : 58,
          justifyContent: 'center',
          pb: 0.5,
          pt: 0.5,
          px: 0.75,
          width: compact ? 52 : 92,
          '&:hover': disabled ? {} : { borderColor: 'primary.main', color: 'primary.main' },
          '& svg': { height: compact ? 22 : 26, width: compact ? 38 : 58 },
        }}
      >
        <ChartTypeGlyph type={value} />
        {!compact && (
          <Typography
            sx={{ fontSize: '10px', lineHeight: 1.2, maxWidth: '100%', textAlign: 'center' }}
            variant="caption"
          >
            {cardTypeLabel(value)}
          </Typography>
        )}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => setAnchor(null)}
        open={Boolean(anchor)}
        slotProps={{ paper: { sx: { maxWidth: 340 } } }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        {types.map((type) => (
          <MenuItem
            key={type}
            onClick={() => {
              onChoose(type);
              setAnchor(null);
            }}
            selected={type === value}
            sx={{ alignItems: 'flex-start', gap: 1.25, py: 1, whiteSpace: 'normal' }}
          >
            <Box
              sx={{
                color: type === value ? 'primary.main' : 'text.secondary',
                flexShrink: 0,
                mt: 0.25,
                '& svg': { height: 24, width: 52 },
              }}
            >
              <ChartTypeGlyph type={type} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: type === value ? 600 : 500 }} variant="body2">
                {cardTypeLabel(type)}
              </Typography>
              <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
                {CARD_TYPE_GUIDANCE[type]}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Where a card sits in the plan, and the two ways to change it.
 *
 * The number is the point: sixteen rows with no positions is a list you can
 * only refer to as "the one under the other one", and the order is the order
 * the board opens in. Dragging is quicker for a long move, the arrows for a
 * short one, and the arrows are also the only way to do it from a keyboard.
 *
 * This column is the drag handle rather than the whole row, because the row
 * carries a text field and a draggable ancestor takes the selection gesture
 * away from it.
 */
export function RowPosition({
  compact = false,
  disabled,
  index,
  isDragging,
  onDragEnd,
  onDragStart,
  onMove,
  title,
  total,
}: {
  compact?: boolean;
  disabled: boolean;
  index: number;
  isDragging?: boolean;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  onMove: (to: number) => void;
  title: string;
  total: number;
}) {
  return (
    <Tooltip title={disabled ? '' : 'Drag to reorder'}>
      <Stack
        alignItems="center"
        draggable={!disabled}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        sx={{
          cursor: disabled ? 'default' : 'grab',
          flexShrink: 0,
          justifyContent: 'center',
          opacity: isDragging ? 0.4 : 1,
          width: compact ? 22 : 30,
          '&:active': disabled ? {} : { cursor: 'grabbing' },
        }}
      >
        <IconButton
          aria-label={`Move ${title} up`}
          disabled={disabled || index === 0}
          onClick={() => onMove(index - 1)}
          size="small"
          sx={{ p: 0 }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: compact ? 16 : 20 }} />
        </IconButton>
        <Typography
          color="text.secondary"
          sx={{ fontSize: compact ? '0.625rem' : undefined, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}
          variant="caption"
        >
          {index + 1}
        </Typography>
        <IconButton
          aria-label={`Move ${title} down`}
          disabled={disabled || index === total - 1}
          onClick={() => onMove(index + 1)}
          size="small"
          sx={{ p: 0 }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: compact ? 16 : 20 }} />
        </IconButton>
      </Stack>
    </Tooltip>
  );
}
